import React, { useCallback } from 'react';

import { PageErrorUi } from 'components';
import Loader from './Loader';
import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';
import { createFormData } from './utils';
import Form from './Form';

/**
 * @type {React.Context<{
 *  data: import('zod').infer<typeof FORM_SCHEMA>,
 *  initialData: import('zod').infer<typeof FORM_SCHEMA>,
 *  errors: Partial<import('zod').infer<typeof FORM_SCHEMA>>,
 *  isDirty: boolean,
 *  isProcessing: boolean,
 *  onChange: (updates: Partial<import('zod').infer<typeof FORM_SCHEMA>>, runValidation?: boolean) => void,
 *  onSave: (callback?: (err: Error, data: any) => void) => void
 * }>}
 */
const DefaultReturnWindowContext = React.createContext();

export function useDefaultReturnWindow() {
  const context = React.useContext(DefaultReturnWindowContext);
  if (!context) {
    throw new Error(
      'useDefaultReturnWindow must be used within a DefaultReturnWindowProvider'
    );
  }
  return context;
}

export function DefaultReturnWindowProvider({ children = null }) {
  const { track } = useSegment();
  const {
    data,
    initialData,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const { isLoading, isError, fetchData } = useFetch(
    'configure.serviceWindow.global',
    {
      onSuccess: data => onSetInitialData(createFormData(data))
    }
  );

  const { isProcessing, mutate } = useMutation(
    'configure.serviceWindow.global',
    'PUT',
    {
      payload: () => ({ ...initialData, ...data }),
      successMessage: 'Successfully updated the return window.',
      errorMessage:
        'Unable to update the return window at the moment. Please try again later.',
      onValidate: () => onValidate(data),
      onSuccess: data => {
        track('FTU Wizard Return Window Configured', {
          days: data.serviceable_days
        });
        onSetInitialData(createFormData(data));
      }
    }
  );

  const onSave = useCallback(callback => mutate({}, callback), [mutate]);

  return (
    <DefaultReturnWindowContext.Provider
      value={{
        data,
        initialData,
        errors,
        isDirty,
        isProcessing,
        onChange,
        onSave
      }}
    >
      <PageErrorUi
        isError={isError}
        onReload={fetchData}
        msg="Unable to load the Return Window data at the moment!"
      >
        <Loader isLoading={isLoading}>
          <Form />
          {children}
        </Loader>
      </PageErrorUi>
    </DefaultReturnWindowContext.Provider>
  );
}
